import type { LucideIcon } from "lucide-react";
import { useId } from "react";
import { useTranslation } from "react-i18next";
import { NavLink } from "react-router-dom";
import type { BackendModuleKey } from "../../lib/backendModuleKeys";
import type { PendingRequestRef } from "../../lib/accessRequests";
import { cn } from "../../lib/utils";

type SidebarNavLinkProps = {
  href: string;
  name: string;
  icon: LucideIcon;
  granted: boolean;
  requestable: boolean;
  moduleKey: BackendModuleKey | null;
  pending: PendingRequestRef | null;
  isActive: boolean;
  collapsed: boolean;
  end?: boolean;
  onNavigate?: () => void;
};

/**
 * DEC-006: navigates to the gated route (ModulePageGate on the page). Restricted rows are
 * greyed with Solicită acces affordance; pending shows on the nav item.
 */
export function SidebarNavLink({
  href,
  name,
  icon: Icon,
  granted,
  requestable,
  pending,
  isActive,
  collapsed,
  end,
  onNavigate,
}: SidebarNavLinkProps) {
  const { t } = useTranslation();
  const restrictedHintId = useId();
  const restricted = !granted;
  const restrictedLabel = t("nav.restrictedAccess", {
    defaultValue: "acces restricționat",
  });

  const linkClass = cn(
    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
    collapsed && "lg:justify-center lg:px-2",
    restricted &&
      "opacity-55 saturate-50 blur-[0.3px] hover:opacity-70 dark:opacity-50",
    isActive && granted
      ? "bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400"
      : isActive && restricted
        ? "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
        : "text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700",
  );

  return (
    <>
      {restricted ? (
        <span id={restrictedHintId} className="sr-only">
          {restrictedLabel}
        </span>
      ) : null}
      <NavLink
        to={href}
        end={end}
        onClick={onNavigate}
        aria-label={name}
        aria-describedby={restricted ? restrictedHintId : undefined}
        title={collapsed ? name : undefined}
        className={linkClass}
        data-restricted={restricted ? "true" : undefined}
        data-testid={restricted ? `nav-restricted-${href}` : undefined}
      >
        <Icon className="w-5 h-5 shrink-0" aria-hidden />
        <span
          className={cn(
            "flex flex-1 flex-wrap items-center gap-2 min-w-0",
            collapsed && "lg:hidden",
          )}
        >
          <span>{name}</span>
          {restricted && requestable && !pending ? (
            <span
              className="text-[10px] uppercase tracking-wide font-semibold text-primary-700 dark:text-primary-400"
            >
              {t("accessRequests.requestButton")}
            </span>
          ) : null}
          {restricted && requestable && pending ? (
            <span className="text-[10px] font-medium text-amber-700 dark:text-amber-300">
              {t("accessRequests.pendingLabel")}
            </span>
          ) : null}
        </span>
      </NavLink>
    </>
  );
}
